/**
 * MySubmissionsPanel — shows the user's own emoji submissions with
 * statuses (pending / approved / rejected) and the admin's note (if any).
 * Opens from the CommunicationEmojisPage header as a side panel.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, CheckCircle2, XCircle, Loader2, Trash2, ImageOff } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:  { icon: Clock,        color: 'text-amber-500', label: 'Pending review' },
  approved: { icon: CheckCircle2, color: 'text-green-500', label: 'Approved & published' },
  rejected: { icon: XCircle,      color: 'text-red-500',   label: 'Not approved' },
};

const MySubmissionsPanel = ({ open, onClose, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxPending, setMaxPending] = useState(5);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiFetch('/api/emojis/submissions/mine');
    setLoading(false);
    if (error) return toast.error(error);
    setItems(data?.items || []);
    if (data?.max_pending) setMaxPending(data.max_pending);
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load, refreshKey]);

  const cancel = async (id) => {
    if (!window.confirm('Withdraw this submission? You can submit again later.')) return;
    setCancelling(id);
    const { error } = await apiFetch(`/api/emojis/submissions/${id}`, { method: 'DELETE' });
    setCancelling(null);
    if (error) return toast.error(error);
    toast.success('Submission withdrawn.');
    load();
  };

  const pendingCount = items.filter((i) => i.status === 'pending').length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="my-submissions-dialog">
        <DialogHeader>
          <DialogTitle>My emoji submissions</DialogTitle>
          <DialogDescription>
            {pendingCount} of {maxPending} pending slots in use.
            Approved emojis appear in the gallery — you&apos;re credited as the submitter.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            You haven&apos;t submitted any emojis yet.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((s) => {
              const meta = STATUS_META[s.status] || STATUS_META.pending;
              const Icon = meta.icon;
              return (
                <div
                  key={s.submission_id}
                  className="flex items-start gap-3 border rounded-md p-3"
                  data-testid={`my-submission-${s.submission_id}`}
                >
                  <div className="w-14 h-14 shrink-0 border rounded bg-white flex items-center justify-center overflow-hidden">
                    {s.image_path ? (
                      <img
                        src={s.image_path.startsWith('http') ? s.image_path : `${API}${s.image_path}`}
                        alt={s.label}
                        className="w-full h-full object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <ImageOff className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium truncate">{s.label}</p>
                      <Badge variant="outline" className={`text-[10px] ${meta.color}`}>
                        <Icon className="h-3 w-3 mr-1 inline" />
                        {meta.label}
                      </Badge>
                    </div>
                    {s.description && (
                      <p className="text-xs text-muted-foreground mt-1 break-words">{s.description}</p>
                    )}
                    {s.admin_note && (
                      <p className={`text-xs mt-1 break-words ${s.status === 'rejected' ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                        <b>Reviewer note:</b> {s.admin_note}
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Submitted {new Date(s.created_at).toLocaleDateString()}
                      {s.reviewed_at && ` · Reviewed ${new Date(s.reviewed_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  {s.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground hover:text-red-500"
                      onClick={() => cancel(s.submission_id)}
                      disabled={cancelling === s.submission_id}
                      title="Withdraw submission"
                      data-testid={`cancel-submission-${s.submission_id}`}
                    >
                      {cancelling === s.submission_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MySubmissionsPanel;
